import { useEffect, useState } from 'react';
import { fbAuth } from '@/constants/endpoints';
import useUserStore from '@/stores/UserStore';
import { Loader2Icon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { APIClient } from '@/lib/ApiClient';
import {
  checkIGConnectionStatus,
  disconnectInstagram,
  reconnectInstagram,
} from '@/hooks/user';

import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

const apiClient = new APIClient();

const Instagram = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Handle Instagram OAuth callback
    const handleInstagramCallback = () => {
      const params = new URLSearchParams(location.search);
      const status = params.get('status');

      // Check if this is a callback from Instagram OAuth
      if (status) {
        if (status === 'success') {
          toast.success('Instagram connected successfully!');
          // Clear URL parameters and reload to get updated user data
          navigate('/settings/instagram', { replace: true });
          window.location.reload();
        } else if (status === 'error') {
          toast.error('Instagram connection failed');
          // Clear URL parameters
          navigate('/settings/instagram', { replace: true });
        }
      }
    };

    handleInstagramCallback();
  }, [location.search, navigate]);

  return (
    <div className='h-full space-y-6'>
      <div className='flex justify-between'>
        <div>
          <h3 className='text-lg font-medium'>Facebook & Instagram</h3>
          <p className='text-sm text-muted-foreground'>
            Manage your connection
          </p>
        </div>
      </div>
      <Separator />
      <InstagramConnection />
    </div>
  );
};

export default Instagram;

const InstagramConnection = () => {
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState(null); // 'connected', 'needs_reconnect', 'not_connected'

  let { user } = useUserStore(
    useShallow((state) => ({
      user: state.user,
    }))
  );

  // Check Instagram connection status
  const checkStatus = async () => {
    setLoading(true);
    try {
      await checkIGConnectionStatus();
      setConnectionStatus('connected');
    } catch (error) {
      if (error.response?.status === 422) {
        setConnectionStatus('needs_reconnect');
      } else if (error.response?.status === 404) {
        setConnectionStatus('not_connected');
      } else {
        console.error('Failed to check Instagram status:', error);
        setConnectionStatus('not_connected');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check current connection status
    checkStatus();
  }, []);

  const fetchRedirectURI = async () => {
    try {
      const response = await apiClient.get(fbAuth, null, true);

      return response.redirect_url;
    } catch (error) {
      // Handle API request errors
      console.error('API Error:', error);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const redirectURI = await fetchRedirectURI();
      window.location.href = redirectURI;
      // Old code for reference
      // let params =
      //   'scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=0,height=0,left=-1000,top=-1000';
      // const popup = window.open(redirectURI, 'Facebook Login', params);
      // const timer = setInterval(function () {
      //   if (popup.closed) {
      //     clearInterval(timer);
      //     dispatch(fetchUser());
      //   }
      // }, 1000);
    } catch (error) {
      console.error('API Error:', error);
      toast.error('Failed to connect Instagram');
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      await disconnectInstagram();
      toast.success('Instagram disconnected successfully');
      setConnectionStatus('not_connected');
    } catch (error) {
      console.error('Failed to disconnect Instagram:', error);
      toast.error('Failed to disconnect Instagram');
    } finally {
      setLoading(false);
    }
  };

  const handleReconnect = async () => {
    setLoading(true);
    try {
      const response = await reconnectInstagram();

      // Check if we got a reconnect URL in the response
      if (response?.data?.reconnect_url) {
        window.location.href = response.data.reconnect_url;
      } else {
        // If no URL, assume it was successful
        toast.success('Instagram reconnected successfully');
        setConnectionStatus('connected');
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to reconnect Instagram:', error);
      toast.error('Failed to reconnect Instagram');
      setLoading(false);
    }
  };

  const defaultPic = `http://graph.facebook.com/${user?.customer?.fb_id}/picture?type=large`;

  if (loading) {
    return (
      <div className='flex items-center justify-center pt-48'>
        <Loader2Icon className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  const name = user?.customer?.ig_name || '';
  const profilePic = user?.customer?.profile_pic_url || defaultPic;

  return (
    <div className='flex items-center justify-center pt-48'>
      {connectionStatus === 'connected' && user?.customer?.ig_name ? (
        <div className='flex flex-col items-center gap-4 text-center'>
          <Avatar className='h-24 w-24'>
            <AvatarImage src={profilePic} alt={name} />
            <AvatarFallback>{name?.charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className='flex flex-col gap-1'>
            <h2 className='text-2xl font-bold'>{name}</h2>
            <span className='text-sm text-muted-foreground'>
              @{user?.customer?.ig_username}
            </span>
          </div>
          <div className='mt-5'>
            <Button
              onClick={handleReconnect}
              disabled={loading}
              className='flex items-center justify-center'
            >
              {loading ? (
                <>
                  <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                  Reconnecting...
                </>
              ) : (
                'Connect / Reconnect'
              )}
            </Button>
            {/* <Button
              onClick={handleDisconnect}
              disabled={loading}
              variant='destructive'
              className='flex items-center justify-center'
            >
              {loading ? (
                <>
                  <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </Button> */}
          </div>
        </div>
      ) : connectionStatus === 'needs_reconnect' ? (
        <div className='flex flex-col items-center'>
          <span>Your Instagram connection needs to be reconnected</span>
          <div className='mt-5'>
            <Button
              onClick={handleReconnect}
              disabled={loading}
              className='flex items-center justify-center'
            >
              {loading ? (
                <>
                  <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                  Reconnecting...
                </>
              ) : (
                'Reconnect Instagram'
              )}
            </Button>
          </div>
        </div>
      ) : (
        <div className='flex flex-col items-center'>
          <span>You have not connected an Instagram Account</span>
          <div className='mt-5'>
            <Button
              onClick={handleConnect}
              disabled={loading}
              className='flex items-center justify-center'
            >
              {loading ? (
                <>
                  <Loader2Icon className='mr-2 h-4 w-4 animate-spin' />
                  Connecting...
                </>
              ) : (
                'Connect Instagram'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
